import { useLayoutEffect, useRef, useState } from 'react'
import { niceScale } from '../inventoree/EndpointCharts'
import { axisDuration, labelStep } from './DashboardCharts'
import type { Bucket } from '../api/productivitee'

/**
 * Graficul de EVOLUTIE al lui Productivitee: o coloana pe treapta (zi,
 * saptamana sau luna), si langa fiecare coloana a perioadei de acum coloana
 * PALIDA a perioadei dinainte.
 *
 * -----------------------------------------------------------------------------
 * DE CE E DESENAT AICI
 * -----------------------------------------------------------------------------
 *
 * Din acelasi motiv ca `DashboardCharts.tsx`, scris acolo pe larg: amCharts a
 * iesit din portal sub auditul bancar din septembrie 2026, si v4 isi deseneaza
 * graficele singur - `alarms/AreaChart.tsx`, `devices/AgentMonitorChart.tsx`,
 * `inventoree/EndpointCharts.tsx`. Nu se aduce o biblioteca pentru un desen.
 *
 * Piesele se iau de unde sunt deja: `niceScale` din EndpointCharts (treptele
 * axei), `axisDuration` si `labelStep` din DashboardCharts (durata scrisa pe
 * axa si raritul etichetelor la cel mult zece). Al doilea dialect pentru
 * aceleasi lucruri ar fi insemnat doua axe care nu se citesc la fel.
 *
 * -----------------------------------------------------------------------------
 * DOUA COLOANE LIPITE, NU DOUA LINII SUPRAPUSE
 * -----------------------------------------------------------------------------
 *
 * "Mai bine sau mai rau" se citeste dintr-o pereche de coloane lipite mai bine
 * decat din doua linii care se incruciseaza: perechea are o singura intrebare
 * - care e mai inalta - si raspunsul se vede si pe un ecran de perete, fara
 * legenda si fara cursor. Cea din urma (perioada dinainte) sta prima si
 * palida, cea de acum a doua si plina: ochiul citeste de la stanga la dreapta,
 * deci de la "atunci" la "acum".
 */

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif'
const AXIS = '#666'
const GRID = 'rgba(0,0,0,0.1)'

/**
 * Cerneala desenului. Albastrul e cel al modulului (`#0998F7`, coloanele
 * tabloului); `median` e linia grupului, iar `reference` e reperul orizontal
 * ("unde erai tu in perioada dinainte"). Amandoua sunt LINII peste coloane, nu
 * inca doua coloane: sunt alt fel de cifra - un prag, nu o masuratoare a
 * treptei - si daca ar arata la fel s-ar citi ca o a treia serie.
 */
export const TREND_COLORS = { now: '#0998F7', before: '#B9BFC6', median: '#7B5EA7', reference: '#4DB36A' } as const

export type Metric = 'share' | 'active_time' | 'working_time'

/**
 * Numele cifrelor, asa cum se citesc pe ecran.
 *
 * Roxana, 21 septembrie 2026: "scoate active share si pune-i active time si
 * eventual (%), dar nu se intelege ce e". "Active share" nu spunea nimanui ce
 * e impartit la ce; cifra se cheama acum cu numele marimii pe care lumea o stie
 * deja - `Active time` - si cu semnul ca e o parte din intreg: `(%)`.
 *
 * Durata ramane `Active time` gol. Cele doua nu poarta niciodata acelasi nume:
 * una e "6h 20m", cealalta "62.5%".
 */
export const METRIC_LABEL: Record<Metric, string> = {
  share: 'Active time (%)',
  active_time: 'Active time',
  working_time: 'Working time',
}

/** Randul care spune, sub cifra, ce e procentul - o data pe ecran, acolo unde apare prima oara. */
export const SHARE_HINT = 'Active time (%) is the part of the working time that was active.'

/** Cifra scrisa pe axa si in balon: procent cu un semn, restul ca durata. */
export function metricText(metric: Metric, value: number): string {
  return metric === 'share' ? `${Math.round(value * 10) / 10}%` : axisDuration(value)
}

/** Latimea panoului - aceeasi socoteala ca `useWidth` din EndpointCharts si DashboardCharts. */
function useWidth(fallback = 320) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [box, width > 0 ? width : fallback] as const
}

/**
 * Bucatile NEINTRERUPTE ale unei serii cu goluri: `[0,1,null,3]` da `[[0,1],[3]]`.
 *
 * O linie trasa peste un gol ar fi legat doua trepte intre care nu se stie
 * nimic, si ar fi aratat exact ca una masurata - de-aia se rupe.
 */
export function segmentsOf(values: (number | null | undefined)[]): number[][] {
  const runs: number[][] = []
  let run: number[] = []
  values.forEach((v, i) => {
    if (v === null || v === undefined) {
      if (run.length > 0) runs.push(run)
      run = []
      return
    }
    run.push(i)
  })
  if (run.length > 0) runs.push(run)
  /* Un punct singur nu face o linie, dar are bulina lui desenata separat. */
  return runs.filter((r) => r.length > 1)
}

function valueOf(bucket: Bucket | undefined | null, metric: Metric): number {
  if (!bucket) return 0
  return metric === 'share' ? bucket.share : bucket[metric]
}

export function TrendChart({ buckets, previous, metric, median, reference, height = 280 }: {
  buckets: Bucket[]
  previous: Bucket[]
  metric: Metric
  /** Mediana grupului pe fiecare treapta, la fel de multe cate trepte sunt; `null` unde n-a raportat nimeni. */
  median?: (number | null)[]
  /** Un reper orizontal - mediana omului in perioada dinainte. */
  reference?: { value: number; label: string }
  height?: number
}) {
  const [box, width] = useWidth()
  const [hover, setHover] = useState<number | null>(null)

  /* Etichetele stau drepte pe zile scurte si inclinate cand sunt multe; jos li
     se tine loc dupa cat sunt de lungi, ca in `VerticalBarChart`. */
  const longest = Math.max(0, ...buckets.map((b) => b.label.length)) * 5.6
  const pad = { top: 12, right: 12, bottom: Math.min(110, Math.round(22 + longest * 0.71)), left: metric === 'share' ? 46 : 86 }
  const plotLeft = pad.left
  const plotRight = Math.max(plotLeft + 1, width - pad.right)
  const plotTop = pad.top
  const plotBottom = Math.max(plotTop + 1, height - pad.bottom)
  const plotW = Math.max(1, plotRight - plotLeft)
  const plotH = Math.max(1, plotBottom - plotTop)

  const medianValues = (median ?? []).filter((v): v is number => v !== null && v !== undefined)
  const highest = Math.max(
    0,
    ...buckets.map((b) => valueOf(b, metric)),
    ...previous.map((b) => valueOf(b, metric)),
    ...medianValues,
    reference ? reference.value : 0,
  )
  /* Procentul are capatul lui firesc la 100: o luna la 40% n-are de ce sa para
     plina fiindca nicio treapta n-a trecut de 40. */
  const { top, step } = metric === 'share' ? { top: 100, step: 20 } : niceScale(highest)
  const band = plotW / Math.max(1, buckets.length)
  const barW = Math.max(2, Math.min(28, (band - 6) / 2))
  const y = (v: number) => plotBottom - (top <= 0 ? 0 : (v / top) * plotH)
  const cx = (i: number) => plotLeft + band * i + band / 2

  const gridlines: number[] = []
  for (let v = 0; v <= top + 1e-9; v += step) gridlines.push(Number(v.toFixed(6)))
  const everyNth = labelStep(buckets.length)
  const tipNow = hover === null ? null : buckets[hover]
  const tipBefore = hover === null ? null : previous[hover]

  return (
    <div ref={box} style={{ width: '100%', height, position: 'relative' }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={`${METRIC_LABEL[metric]} over time`}>
          {gridlines.map((v) => (
            <g key={v}>
              <line x1={plotLeft} x2={plotRight} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1} />
              <text x={plotLeft - 6} y={y(v) + 4} textAnchor="end" style={{ fill: AXIS, fontSize: 11, fontFamily: FONT }}>
                {metricText(metric, v)}
              </text>
            </g>
          ))}
          {buckets.map((b, i) => {
            const before = valueOf(previous[i], metric)
            const now = valueOf(b, metric)
            return (
              <g key={`pair-${b.key}`}>
                <rect
                  x={cx(i) - barW - 1}
                  y={y(before)}
                  width={barW}
                  height={Math.max(0, plotBottom - y(before))}
                  fill={TREND_COLORS.before}
                  fillOpacity={0.55}
                />
                <rect
                  x={cx(i) + 1}
                  y={y(now)}
                  width={barW}
                  height={Math.max(0, plotBottom - y(now))}
                  fill={TREND_COLORS.now}
                  fillOpacity={0.75}
                />
              </g>
            )
          })}
          {/*
            Reperul: unde era OMUL in perioada dinainte. O linie intrerupta,
            orizontala, cu eticheta la capatul din stanga - un prag, nu o serie.
          */}
          {reference ? (
            <g>
              <line
                x1={plotLeft}
                x2={plotRight}
                y1={y(reference.value)}
                y2={y(reference.value)}
                stroke={TREND_COLORS.reference}
                strokeWidth={1.5}
                strokeDasharray="5 4"
              />
              {/* Eticheta sta la capatul din DREAPTA, deasupra liniei: la stanga
                  cadea peste prima pereche de coloane si peste treptele axei. */}
              <text x={plotRight - 2} y={y(reference.value) - 5} textAnchor="end" style={{ fill: TREND_COLORS.reference, fontSize: 10, fontFamily: FONT }}>
                {reference.label}
              </text>
            </g>
          ) : null}
          {/*
            Mediana grupului, treapta cu treapta. Se rupe acolo unde nimeni
            n-a raportat: un drum tras peste un gol ar fi inventat o cifra.
          */}
          {median
            ? segmentsOf(median).map((run) => (
                <polyline
                  key={`median-${run[0]}`}
                  points={run.map((i) => `${cx(i)},${y(median[i] as number)}`).join(' ')}
                  fill="none"
                  stroke={TREND_COLORS.median}
                  strokeWidth={2}
                />
              ))
            : null}
          {median?.map((v, i) =>
            v === null || v === undefined ? null : (
              <circle key={`median-dot-${i}`} cx={cx(i)} cy={y(v)} r={3} fill="#fff" stroke={TREND_COLORS.median} strokeWidth={2} />
            ),
          )}
          {buckets.map((b, i) =>
            i % everyNth === 0 ? (
              <text
                key={`tick-${b.key}`}
                x={cx(i)}
                y={plotBottom + 8}
                textAnchor="end"
                transform={`rotate(-45 ${cx(i)} ${plotBottom + 8})`}
                style={{ fill: AXIS, fontSize: 10, fontFamily: FONT }}
              >
                {b.label}
              </text>
            ) : null,
          )}
          {buckets.map((b, i) => (
            <rect
              key={`hit-${b.key}`}
              x={plotLeft + band * i}
              y={plotTop}
              width={band}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
      )}
      {tipNow && hover !== null && (
        <div
          className="productivitee-chart-tip"
          style={{
            position: 'absolute',
            left: Math.min(Math.max(4, cx(hover) + 10), Math.max(4, width - 190)),
            top: Math.max(2, y(Math.max(valueOf(tipNow, metric), valueOf(tipBefore, metric))) - 62),
            background: 'rgba(0,0,0,0.8)',
            color: '#fff',
            borderRadius: 6,
            padding: '4px 8px',
            fontFamily: FONT,
            fontSize: 12,
            lineHeight: '16px',
            pointerEvents: 'none',
            maxWidth: 200,
            zIndex: 5,
          }}
        >
          <div style={{ fontWeight: 'bold' }}>{tipNow.label}</div>
          <div>Now: {metricText(metric, valueOf(tipNow, metric))}</div>
          <div>Before: {metricText(metric, valueOf(tipBefore, metric))}</div>
          {median && median[hover] !== null && median[hover] !== undefined ? (
            <div style={{ color: '#D9CCEE' }}>Group median: {metricText(metric, median[hover] as number)}</div>
          ) : null}
          {/* Zilele CU RAPORT din treapta - fara ele o coloana scunda arata la
              fel fie ca s-a lucrat putin, fie ca nu s-a masurat nimic. */}
          <div style={{ opacity: 0.8 }}>{tipNow.days} reported {tipNow.days === 1 ? 'day' : 'days'}</div>
        </div>
      )}
    </div>
  )
}

/** Seriile desenului, scrise - culoarea nu e singurul semn, ca la tabloul modulului. */
export function TrendLegend({ now, before, median, reference }: { now: string; before: string; median?: string; reference?: string }) {
  return (
    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 11, color: '#666', alignItems: 'center', minHeight: 22, flexWrap: 'wrap' }}>
      <span>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: TREND_COLORS.before, opacity: 0.55, marginRight: 5 }} /> {before}
      </span>
      <span>
        <span style={{ display: 'inline-block', width: 10, height: 10, background: TREND_COLORS.now, opacity: 0.75, marginRight: 5 }} /> {now}
      </span>
      {median ? (
        <span>
          <span style={{ display: 'inline-block', width: 14, height: 2, background: TREND_COLORS.median, marginRight: 5, verticalAlign: 'middle' }} /> {median}
        </span>
      ) : null}
      {reference ? (
        <span>
          <span
            style={{ display: 'inline-block', width: 14, height: 0, borderTop: `2px dashed ${TREND_COLORS.reference}`, marginRight: 5, verticalAlign: 'middle' }}
          />{' '}
          {reference}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Sageata de "mai bine / mai rau", cu semnul in text, nu numai in culoare.
 *
 * Verdele si rosul singure nu spun nimic celui care nu le deosebeste, si nimic
 * pe o imprimanta alb-negru: langa culoare sta mereu `+` sau `-`.
 */
export function Delta({ value, unit = '%', format }: { value: number; unit?: string; format?(size: number): string }) {
  const rounded = Math.round(value * 10) / 10
  if (rounded === 0) return <span style={{ color: '#888' }}>=</span>
  const up = rounded > 0
  return (
    <span style={{ color: up ? '#4DB36A' : '#DE4C4F', whiteSpace: 'nowrap' }}>
      <i className={`fa fa-arrow-${up ? 'up' : 'down'}`} aria-hidden="true" /> {up ? '+' : '-'}
      {format ? format(Math.abs(rounded)) : `${Math.abs(rounded)}${unit}`}
    </span>
  )
}
